import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';

import CustomBackdrop from '../../../components/CustomBackdrop/CustomBackdrop';
import {
  useCortexFeedbackPipedriveBridgeMutation,
  useCreatePdOrganizationMutation,
  useFinishCompanyValidationMutation,
  useGetBuyAndBuildPlatformsQuery,
  useGetCompanyQuery,
  usePrecalculateAllKpisMutation,
} from '../../../gql/generated/graphql';
import { useSnackbar } from '../../../providers/SnackbarProvider';
import { paths } from '../../../routes/paths';
import BIDPlatform from '../../ValidationPage/components/BIDPlatform/BIDPlatform';

interface Props {
  companyId: number;
  userId: number | null;
  authId: string | null;
  onReleaseLock: () => Promise<void>;
}

const CompanyFinishStep = ({ companyId, userId, authId, onReleaseLock }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [platform, setPlatform] = useState<string | null>(null);

  const { data: companyData } = useGetCompanyQuery({ variables: { companyId } });
  const companyPipedriveId = companyData?.getCompany?.edges?.[0]?.node?.pipedriveId;
  const hasPipedriveId = !!companyPipedriveId;

  const [formData, setFormData] = useState({
    newPlatform: false,
    addOnExisting: false,
    addOnFuture: false,
    saas: false,
    venture: false,
    none: false,
    partOfGroup: false,
    otherOwnership: false,
    sellsHardware: false,
    tooMuchConsulting: false,
    reseller: false,
    wrongBusinessModel: false,
    b2c: false,
    wrongGeography: false,
    tooBig: false,
    tooSmall: false,
    createOrganization: hasPipedriveId,
    createDeal: false,
    selectValue: '',
    note: '',
  });

  useEffect(() => {
    if (hasPipedriveId && !formData.createOrganization) {
      setFormData(prev => ({ ...prev, createOrganization: true }));
    }
  }, [hasPipedriveId]);

  const isNoteEnabled = formData.createOrganization || formData.createDeal || hasPipedriveId;

  const { data: platformsData } = useGetBuyAndBuildPlatformsQuery({
    variables: { sortForTargetId: companyId },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });
  const platforms = platformsData?.getBuyAndBuildPlatforms?.edges?.map(edge => edge?.node);

  const [createPDOrganization, { loading: createPDLoading }] = useCreatePdOrganizationMutation();
  const [precalculateAllKpis, { loading: kpisLoading }] = usePrecalculateAllKpisMutation();
  const [addBidPlatform, { loading: bidLoading }] = useCortexFeedbackPipedriveBridgeMutation();
  const [finishCompanyValidation, { loading: finishLoading }] = useFinishCompanyValidationMutation();

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === 'createOrganization' && hasPipedriveId) return;
    setFormData(prev => ({ ...prev, [event.target.name]: event.target.checked }));
  };

  const handleSubmit = async () => {
    if (!userId || !authId) return;
    try {
      // Call V2 finish mutation
      const result = await finishCompanyValidation({
        variables: { companyId, userId, authId },
      });

      if (!result.data?.finishCompanyValidation?.success) {
        showSnackbar(result.data?.finishCompanyValidation?.message || 'Finish failed', 'error');
        return;
      }

      // Pipedrive integration (same as existing flow)
      createPDOrganization({ variables: { companyId, authId, ...formData } });
      precalculateAllKpis({ variables: { companyId } });

      if (platform) {
        addBidPlatform({ variables: { addOnForBidId: parseInt(platform), companyId } });
      }

      await onReleaseLock();
      showSnackbar('Company validation complete', 'success');
      navigate(paths.crawlerInbox);
    } catch (error) {
      Sentry.captureException(error);
      showSnackbar('Error completing validation', 'error');
    }
  };

  const isAddOnDropdownEnabled = formData.addOnExisting || formData.addOnFuture;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 0, border: '1px solid var(--border-subtle)', boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('validation.finishStep.relevantHeader')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              {(['newPlatform', 'addOnExisting', 'addOnFuture', 'saas', 'venture', 'none'] as const).map(name => (
                <FormControlLabel
                  key={name}
                  control={
                    <Switch
                      size="small"
                      name={name}
                      checked={formData[name] as boolean}
                      onChange={handleSwitchChange}
                    />
                  }
                  label={t(`validation.finishStep.${name}Switch`)}
                />
              ))}
            </FormGroup>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 0, border: '1px solid var(--border-subtle)', boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('validation.finishStep.irreleveantHeader')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              {(
                [
                  'partOfGroup',
                  'otherOwnership',
                  'sellsHardware',
                  'tooMuchConsulting',
                  'reseller',
                  'wrongBusinessModel',
                  'b2c',
                  'wrongGeography',
                  'tooBig',
                  'tooSmall',
                ] as const
              ).map(name => (
                <FormControlLabel
                  key={name}
                  control={
                    <Switch
                      size="small"
                      name={name}
                      checked={formData[name] as boolean}
                      onChange={handleSwitchChange}
                    />
                  }
                  label={t(`validation.finishStep.${name}Switch`)}
                />
              ))}
            </FormGroup>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 0, border: '1px solid var(--border-subtle)', boxShadow: 'none' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('validation.finishStep.pipedriveHeader')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    name="createOrganization"
                    checked={formData.createOrganization}
                    onChange={handleSwitchChange}
                    disabled={hasPipedriveId}
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    {t('validation.finishStep.createOrganizationSwitch')}
                    {hasPipedriveId && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (PipedriveID exists)
                      </Typography>
                    )}
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch size="small" name="createDeal" checked={formData.createDeal} onChange={handleSwitchChange} />
                }
                label={t('validation.finishStep.createDealSwitch')}
              />
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item md={4} xs={12}>
                    <BIDPlatform
                      platform={platform}
                      platforms={platforms}
                      onHandleChangePlatform={(e: SelectChangeEvent) => setPlatform(e.target.value)}
                      disabled={!isAddOnDropdownEnabled}
                    />
                  </Grid>
                  <Grid item md={8} xs={12}>
                    <TextField
                      disabled={!isNoteEnabled}
                      name="note"
                      placeholder={t('validation.finishStep.pipedriveNotePlaceholder')}
                      multiline
                      rows={5}
                      fullWidth
                      value={formData.note}
                      onChange={e => setFormData(prev => ({ ...prev, note: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </Box>
            </FormGroup>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Box textAlign="center">
            <Button variant="contained" onClick={handleSubmit} disabled={finishLoading}>
              Complete Company Validation
            </Button>
          </Box>
        </Grid>
      </Grid>
      <CustomBackdrop open={createPDLoading || kpisLoading || bidLoading || finishLoading} />
    </Box>
  );
};

export default CompanyFinishStep;
